/**
 * Text Component
 * Typography component for rendering text
 */

import React, { forwardRef } from 'react';
import { Box } from './Box';

/**
 * Text Component
 * A component for rendering text with typography styles
 * 
 * @example
 * <Text fontSize="lg" fontWeight="bold" color="brand.500">
 *   Hello World
 * </Text>
 */
export const Text = forwardRef(({
  as = 'p',
  noOfLines,
  isTruncated,
  align,
  textAlign,
  casing,
  textTransform,
  decoration,
  textDecoration,
  ...props
}, ref) => {
  // Handle noOfLines (line clamping)
  const lineClampStyles = noOfLines ? {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: noOfLines,
  } : {};

  // Handle isTruncated (single line ellipsis)
  const truncateStyles = isTruncated && !noOfLines ? {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } : {};

  return (
    <Box
      ref={ref}
      as={as}
      textAlign={align || textAlign}
      textTransform={casing || textTransform}
      textDecoration={decoration || textDecoration}
      sx={{
        ...lineClampStyles,
        ...truncateStyles,
      }}
      {...props}
    />
  );
});

Text.displayName = 'Text';

/**
 * Heading Component
 * Text styled as a heading
 */
export const Heading = forwardRef(({
  as = 'h2',
  size = 'xl',
  ...props
}, ref) => {
  const sizeMap = {
    '4xl': { fontSize: '6xl', lineHeight: 'none', fontWeight: 'bold' },
    '3xl': { fontSize: '5xl', lineHeight: 'shorter', fontWeight: 'bold' },
    '2xl': { fontSize: '4xl', lineHeight: 'shorter', fontWeight: 'bold' },
    xl: { fontSize: '3xl', lineHeight: 'short', fontWeight: 'bold' },
    lg: { fontSize: '2xl', lineHeight: 'short', fontWeight: 'bold' },
    md: { fontSize: 'xl', lineHeight: 'base', fontWeight: 'bold' },
    sm: { fontSize: 'md', lineHeight: 'base', fontWeight: 'bold' },
    xs: { fontSize: 'sm', lineHeight: 'base', fontWeight: 'bold' },
  };

  const sizeStyles = sizeMap[size] || sizeMap.xl;

  return (
    <Text
      ref={ref}
      as={as}
      fontWeight={sizeStyles.fontWeight}
      fontSize={sizeStyles.fontSize}
      lineHeight={sizeStyles.lineHeight}
      {...props}
    />
  );
});

Heading.displayName = 'Heading';

export default Text;
